import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from "@mui/icons-material/Refresh";
import { authenticatedFetch } from "../../utils/api";

interface DataIntegrityResult {
  orphaned_employee_shift_details: Array<{
    id: number;
    program_id: number;
    dia: string;
    gauge: string;
    machine_type: string;
    weight: number;
  }>;
  completed_weight_discrepancies: Array<{
    program_id: number;
    dia: number;
    gauge: string;
    program_type: string;
    stored_weight: number;
    calculated_weight: number;
    difference: number;
  }>;
  summary: {
    checked_at: string;
    total_program_details: number;
    total_employee_shift_details: number;
    orphaned_count: number;
    discrepancy_count: number;
    has_issues: boolean;
  };
}

interface DataIntegrityFixResult {
  success: boolean;
  orphaned_fixed: number;
  discrepancies_fixed: number;
  fixed_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface OrganizationSettings {
  id?: number;
  gstin: string;
  phone_number: string;
  org_name: string;
  address: string;
  email: string;
  state_code: string;
  bank_name_and_branch: string;
  bank_account_number: string;
  ifsc_code: string;
  cgst_percentage: number;
  sgst_percentage: number;
  rate_per_kg: number;
  created_at?: string;
  updated_at?: string;
}

const orgSettingsSchema = z.object({
  gstin: z
    .string()
    .min(1, "GSTIN is required")
    .regex(/^[0-9A-Z]{15}$/, "GSTIN must be exactly 15 alphanumeric characters"),
  phone_number: z.string().min(1, "Phone number is required"),
  org_name: z.string().min(1, "Organization name is required"),
  address: z.string().min(1, "Address is required"),
  email: z.string().min(1, "Email is required").email("Invalid email format"),
  state_code: z.string().min(1, "State code is required"),
  bank_name_and_branch: z.string().min(1, "Bank name & branch is required"),
  bank_account_number: z.string().min(1, "Bank account number is required"),
  ifsc_code: z
    .string()
    .min(1, "IFSC code is required")
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Invalid IFSC code format"),
  cgst_percentage: z
    .number()
    .min(0, "CGST must be at least 0")
    .max(100, "CGST cannot exceed 100"),
  sgst_percentage: z
    .number()
    .min(0, "SGST must be at least 0")
    .max(100, "SGST cannot exceed 100"),
  rate_per_kg: z
    .number()
    .min(0, "Rate must be at least 0"),
});

type OrgSettingsFormData = z.infer<typeof orgSettingsSchema>;

const defaultValues: OrgSettingsFormData = {
  gstin: "",
  phone_number: "",
  org_name: "",
  address: "",
  email: "",
  state_code: "",
  bank_name_and_branch: "",
  bank_account_number: "",
  ifsc_code: "",
  cgst_percentage: 2.5,
  sgst_percentage: 2.5,
  rate_per_kg: 100,
};

export default function OrgSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [isNewSettings, setIsNewSettings] = useState(false);
  
  // Data integrity state
  const [integrityChecking, setIntegrityChecking] = useState(false);
  const [integrityFixing, setIntegrityFixing] = useState(false);
  const [integrityResult, setIntegrityResult] = useState<DataIntegrityResult | null>(null);
  const [integrityFixResult, setIntegrityFixResult] = useState<DataIntegrityFixResult | null>(null);
  const [integrityError, setIntegrityError] = useState<string>("");

  const { control, handleSubmit, reset, formState: { errors } } = useForm<OrgSettingsFormData>({
    resolver: zodResolver(orgSettingsSchema),
    defaultValues,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`${API_URL}/organization_settings`);

      if (response.status === 404) {
        // No settings configured yet
        setSettings(null);
        setIsNewSettings(true);
        setIsEditing(true);
        reset(defaultValues);
      } else if (!response.ok) {
        throw new Error("Failed to fetch organization settings");
      } else {
        const data = await response.json();
        setSettings(data);
        setIsNewSettings(false);
        reset({
          gstin: data.gstin || "",
          phone_number: data.phone_number || "",
          org_name: data.org_name || "",
          address: data.address || "",
          email: data.email || "",
          state_code: data.state_code || "",
          bank_name_and_branch: data.bank_name_and_branch || "",
          bank_account_number: data.bank_account_number || "",
          ifsc_code: data.ifsc_code || "",
          cgst_percentage: parseFloat(data.cgst_percentage) || 2.5,
          sgst_percentage: parseFloat(data.sgst_percentage) || 2.5,
          rate_per_kg: parseFloat(data.rate_per_kg) || 100,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: OrgSettingsFormData) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const method = isNewSettings ? "POST" : "PUT";
      const response = await authenticatedFetch(`${API_URL}/organization_settings`, {
        method,
        body: JSON.stringify({ organization_setting: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData === "object"
            ? Object.entries(errorData)
                .map(([key, value]) => `${key}: ${value}`)
                .join(", ")
            : JSON.stringify(errorData)
        );
      }

      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setIsNewSettings(false);
      setIsEditing(false);
      setSuccess("Organization settings saved successfully!");
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (settings) {
      reset({
        gstin: settings.gstin || "",
        phone_number: settings.phone_number || "",
        org_name: settings.org_name || "",
        address: settings.address || "",
        email: settings.email || "",
        state_code: settings.state_code || "",
        bank_name_and_branch: settings.bank_name_and_branch || "",
        bank_account_number: settings.bank_account_number || "",
        ifsc_code: settings.ifsc_code || "",
        cgst_percentage: parseFloat(String(settings.cgst_percentage)) || 2.5,
        sgst_percentage: parseFloat(String(settings.sgst_percentage)) || 2.5,
        rate_per_kg: parseFloat(String(settings.rate_per_kg)) || 100,
      });
      setIsEditing(false);
    }
    setError("");
  };

  const checkDataIntegrity = async () => {
    try {
      setIntegrityChecking(true);
      setIntegrityError("");
      setIntegrityResult(null);
      setIntegrityFixResult(null);

      const response = await authenticatedFetch(`${API_URL}/admin/data_integrity`);
      if (!response.ok) {
        throw new Error("Failed to check data integrity");
      }

      const data = await response.json();
      setIntegrityResult(data);
    } catch (err) {
      setIntegrityError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIntegrityChecking(false);
    }
  };

  const fixDataIntegrity = async () => {
    if (!window.confirm("Are you sure you want to fix all data integrity issues? This will:\n\n1. Soft-delete orphaned employee shift details\n2. Recalculate all program completion weights\n\nThis action cannot be undone.")) {
      return;
    }

    try {
      setIntegrityFixing(true);
      setIntegrityError("");

      const response = await authenticatedFetch(`${API_URL}/admin/data_integrity/fix`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to fix data integrity issues");
      }

      const data = await response.json();
      setIntegrityFixResult(data);
      setIntegrityResult(null);
      
      // Re-check to show updated status
      await checkDataIntegrity();
    } catch (err) {
      setIntegrityError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIntegrityFixing(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Card sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Typography variant={isMobile ? "h6" : "h5"} component="h2">
              {isNewSettings ? "Configure Organization Settings" : "Organization Settings"}
            </Typography>
            {!isEditing && settings && (
              <Button
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                variant="contained"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Edit Settings
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {isEditing ? (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              {/* Organization Details Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Organization Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="org_name"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Organization Name"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="gstin"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="GSTIN"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || "15-character alphanumeric"}
                          disabled={saving}
                          inputProps={{ style: { textTransform: "uppercase" } }}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="phone_number"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Phone Number"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Email"
                          type="email"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="state_code"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="State Code"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="address"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Address"
                          fullWidth
                          multiline
                          rows={3}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Bank Details Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <AccountBalanceIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Bank Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="bank_name_and_branch"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Bank Name & Branch"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="bank_account_number"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Bank Account Number"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="ifsc_code"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="IFSC Code"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || "11-character code (e.g., SBIN0001234)"}
                          disabled={saving}
                          inputProps={{ style: { textTransform: "uppercase" } }}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Tax Details Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    Tax Configuration
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="cgst_percentage"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <TextField
                          {...field}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          label="CGST Percentage"
                          type="number"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="sgst_percentage"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <TextField
                          {...field}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          label="SGST Percentage"
                          type="number"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={saving}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ step: "0.01", min: "0", max: "100" }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="rate_per_kg"
                      control={control}
                      render={({ field: { onChange, value, ...field }, fieldState }) => (
                        <TextField
                          {...field}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                          label="Default Rate per kg"
                          type="number"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || "Default rate for uninvoiced deliveries in Finance"}
                          disabled={saving}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/kg</InputAdornment>,
                          }}
                          inputProps={{ step: "0.01", min: "0" }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 3 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  fullWidth
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
                {!isNewSettings && (
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    fullWidth
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
            </Box>
          ) : (
            <Box>
              {/* Organization Details Display */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Organization Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Organization Name
                    </Typography>
                    <Typography variant="body1">{settings?.org_name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      GSTIN
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                      {settings?.gstin}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone Number
                    </Typography>
                    <Typography variant="body1">{settings?.phone_number}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{settings?.email}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      State Code
                    </Typography>
                    <Typography variant="body1">{settings?.state_code}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                      {settings?.address}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Bank Details Display */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <AccountBalanceIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Bank Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Bank Name & Branch
                    </Typography>
                    <Typography variant="body1">{settings?.bank_name_and_branch}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Bank Account Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                      {settings?.bank_account_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      IFSC Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                      {settings?.ifsc_code}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Tax Details Display */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    Tax Configuration
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      CGST Percentage
                    </Typography>
                    <Typography variant="body1">{settings?.cgst_percentage}%</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      SGST Percentage
                    </Typography>
                    <Typography variant="body1">{settings?.sgst_percentage}%</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Default Rate per kg
                    </Typography>
                    <Typography variant="body1">₹{settings?.rate_per_kg}/kg</Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Data Integrity Section */}
      <Card sx={{ mt: 3, mb: 4 }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <BuildIcon color="primary" />
            <Typography variant={isMobile ? "h6" : "h5"} component="h2" color="primary">
              Data Integrity
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Check and fix data integrity issues such as orphaned records or weight calculation discrepancies.
            This is useful if you notice inconsistencies between different pages showing production weights.
          </Typography>

          {integrityError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setIntegrityError("")}>
              {integrityError}
            </Alert>
          )}

          {integrityFixResult && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setIntegrityFixResult(null)}>
              Fixed {integrityFixResult.orphaned_fixed} orphaned records and {integrityFixResult.discrepancies_fixed} weight discrepancies.
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={integrityChecking ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={checkDataIntegrity}
              disabled={integrityChecking || integrityFixing}
            >
              {integrityChecking ? "Checking..." : "Check Data Integrity"}
            </Button>
            {integrityResult?.summary.has_issues && (
              <Button
                variant="contained"
                color="warning"
                startIcon={integrityFixing ? <CircularProgress size={20} color="inherit" /> : <BuildIcon />}
                onClick={fixDataIntegrity}
                disabled={integrityChecking || integrityFixing}
              >
                {integrityFixing ? "Fixing..." : "Fix All Issues"}
              </Button>
            )}
          </Stack>

          {integrityResult && (
            <Box>
              {/* Summary */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: integrityResult.summary.has_issues ? "warning.light" : "success.light",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  {integrityResult.summary.has_issues ? (
                    <WarningIcon color="warning" />
                  ) : (
                    <CheckCircleIcon color="success" />
                  )}
                  <Typography variant="subtitle1" fontWeight="bold">
                    {integrityResult.summary.has_issues
                      ? `Found ${integrityResult.summary.orphaned_count + integrityResult.summary.discrepancy_count} issues`
                      : "All checks passed!"}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Checked {integrityResult.summary.total_program_details} program details and{" "}
                  {integrityResult.summary.total_employee_shift_details} shift details
                </Typography>
              </Box>

              {/* Orphaned Records */}
              {integrityResult.orphaned_employee_shift_details.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Orphaned Employee Shift Details ({integrityResult.orphaned_employee_shift_details.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    These records have a deleted parent shift but weren't properly cleaned up.
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: "auto", bgcolor: "grey.100", p: 1, borderRadius: 1 }}>
                    {integrityResult.orphaned_employee_shift_details.map((item) => (
                      <Typography key={item.id} variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                        ID: {item.id} | Program: {item.program_id} | {item.dia}/{item.gauge} {item.machine_type} | {item.weight} kg
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Weight Discrepancies */}
              {integrityResult.completed_weight_discrepancies.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Weight Discrepancies ({integrityResult.completed_weight_discrepancies.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    These program details have stored weights that don't match calculated values.
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: "auto", bgcolor: "grey.100", p: 1, borderRadius: 1 }}>
                    {integrityResult.completed_weight_discrepancies.map((item, index) => (
                      <Typography key={index} variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                        Program {item.program_id} | {item.dia}/{item.gauge} {item.program_type} | 
                        Stored: {item.stored_weight} kg vs Calculated: {item.calculated_weight} kg (diff: {item.difference} kg)
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

